/**
 * Frame Database Operations
 * 
 * Handles caching of frame data for offline access
 */

import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getDB } from './init';
import { STORE_NAMES, LocalFrame } from './schema';
import { getEffectiveApiBaseUrl } from '../config/terminal-urls';


/**
 * Save a frame to IndexedDB
 * @throws Error if database operation fails
 */
/**
 * Save a frame to IndexedDB
 * @throws Error if database operation fails
 */
export async function addFrame(frame: LocalFrame): Promise<void> {
  try {
    const db = await getDB();
    await db.put(STORE_NAMES.FRAMES, frame);
  } catch (error) {
    console.error('Error saving frame to IndexedDB:', error);

    // Handle quota exceeded
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      throw new Error('Database storage quota exceeded. Please clear app data.');
    }

    // Handle database errors
    if (error instanceof Error) {
      throw new Error(`Failed to save frame: ${error.message}`);
    }

    throw new Error('Failed to save frame to database');
  }
}

// Alias for backward compatibility if needed, though not strictly required by index.ts
export const saveFrame = addFrame;

/**
 * Save multiple frames to IndexedDB
 * @throws Error if database operation fails
 */
export async function addFrames(frames: LocalFrame[]): Promise<void> {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAMES.FRAMES, 'readwrite');

    await Promise.all([
      ...frames.map(frame => tx.store.put(frame)),
      tx.done,
    ]);
  } catch (error) {
    console.error('Error saving frames to IndexedDB:', error);

    // Handle quota exceeded
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      throw new Error('Database storage quota exceeded. Please clear app data.');
    }

    // Handle database errors
    if (error instanceof Error) {
      throw new Error(`Failed to save frames: ${error.message}`);
    }

    throw new Error('Failed to save frames to database');
  }
}

/**
 * Get a frame by ID
 * @returns Frame if found, undefined otherwise
 */
export async function getFrame(frameId: string): Promise<LocalFrame | undefined> {
  try {
    const db = await getDB();
    return db.get(STORE_NAMES.FRAMES, frameId);
  } catch (error) {
    console.error('Error getting frame from IndexedDB:', error);
    return undefined;
  }
}

/**
 * Get all frames
 * @returns Array of frames (empty array if error occurs)
 */
export async function getAllFrames(): Promise<LocalFrame[]> {
  try {
    const db = await getDB();
    return db.getAll(STORE_NAMES.FRAMES);
  } catch (error) {
    console.error('Error getting all frames from IndexedDB:', error);
    return [];
  }
}

/**
 * Get frames by category
 */
export async function getFramesByCategory(categoryId: string): Promise<LocalFrame[]> {
  try {
    const db = await getDB();
    return db.getAllFromIndex(STORE_NAMES.FRAMES, 'categoryId', categoryId);
  } catch (error) {
    console.error('Error getting frames by category from IndexedDB:', error);
    return [];
  }
}

/**
 * Get active frames
 */
export async function getActiveFrames(): Promise<LocalFrame[]> {
  try {
    const db = await getDB();
    const allFrames = await db.getAll(STORE_NAMES.FRAMES);
    return allFrames.filter(frame => frame.isActive);
  } catch (error) {
    console.error('Error getting active frames from IndexedDB:', error);
    return [];
  }
}

/**
 * Update a frame
 */
export async function updateFrame(frameId: string, updates: Partial<LocalFrame>): Promise<void> {
  try {
    const db = await getDB();
    const frame = await db.get(STORE_NAMES.FRAMES, frameId);
    if (!frame) {
      throw new Error(`Frame ${frameId} not found`);
    }
    await db.put(STORE_NAMES.FRAMES, { ...frame, ...updates });
  } catch (error) {
    console.error('Error updating frame in IndexedDB:', error);
    throw error;
  }
}

/**
 * Estimate storage size used by frames
 */
export async function getFramesStorageSize(): Promise<number> {
  try {
    const frames = await getAllFrames();
    let size = 0;
    for (const frame of frames) {
      if (frame.imageBlob) size += frame.imageBlob.size;
      if (frame.thumbnailBlob) size += frame.thumbnailBlob.size;
      // Rough estimate for metadata
      size += 1024;
    }
    return size;
  } catch (error) {
    console.error('Error calculating frames storage size:', error);
    return 0;
  }
}

/**
 * Get frames by paper type
 * @returns Array of frames matching the paper type (empty array if error occurs)
 */
export async function getFramesByPaperType(
  paperType: 'collage_4x6' | 'strip'
): Promise<LocalFrame[]> {
  try {
    const db = await getDB();
    const allFrames = await db.getAll(STORE_NAMES.FRAMES);
    return allFrames.filter(frame => frame.paperType === paperType && frame.isActive);
  } catch (error) {
    console.error('Error getting frames by paper type from IndexedDB:', error);
    return [];
  }
}

/**
 * Delete a frame
 */
export async function deleteFrame(frameId: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAMES.FRAMES, frameId);
}

/**
 * Clear all frames
 */
export async function clearFrames(): Promise<void> {
  const db = await getDB();
  await db.clear(STORE_NAMES.FRAMES);
}

/**
 * Refresh frame cache: clear IndexedDB frames store and fetch latest from API for both paper types.
 * Use after uploading new frames in admin or to fix stale frame list in edit terminal.
 */
export async function refreshFramesCache(): Promise<void> {
  await clearFrames();
  await loadFrames('collage_4x6');
  await loadFrames('strip');
}

/**
 * Fetch frame image from URL and convert to Blob
 * @param url - The URL of the frame image
 * @returns Blob containing the image data
 * @throws Error if fetch fails or response is not ok
 */
export async function fetchFrameImage(url: string): Promise<Blob> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.blob();
  } catch (error) {
    console.error(`Failed to fetch frame image from ${url}:`, error);
    if (error instanceof Error) {
      throw new Error(`Failed to fetch frame image: ${error.message}`);
    }
    throw new Error('Failed to fetch frame image');
  }
}

/**
 * Load frames for a specific paper type
 * Attempts to load from cache first, then falls back to API
 * 
 * @param paperType - The paper type to filter frames by
 * @returns Array of LocalFrame objects
 * @throws Error if both cache and API fail
 */
export async function loadFrames(
  paperType: 'collage_4x6' | 'strip'
): Promise<LocalFrame[]> {
  // Try local cache first
  const cachedFrames = await getFramesByPaperType(paperType);
  if (cachedFrames.length > 0) {
    console.log(`Loaded ${cachedFrames.length} frames from cache for ${paperType}`);
    return cachedFrames;
  }

  // Fetch from API
  try {
    console.log(`Fetching frames from API for ${paperType}`);
    const apiClient = createPhotoBoothAPI({
      baseUrl: getEffectiveApiBaseUrl(),
    });

    // Set auth token if available (required for authenticated endpoints)
    try {
      const deviceToken = localStorage.getItem('photobooth_device_token');
      const cashierToken = localStorage.getItem('photobooth_auth_token');
      const token = deviceToken || cashierToken;
      
      if (token && apiClient.setAuthToken) {
        apiClient.setAuthToken(token);
      }
    } catch (error) {
      console.warn('Failed to retrieve auth token from localStorage:', error);
      // Continue without token - API will return 401 if authentication is required
    }

    const response = await apiClient.frames.getByPaperType(paperType);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to load frames from API');
    }

    // Convert API frames to LocalFrames and cache
    const localFrames: LocalFrame[] = [];

    for (const apiFrame of response.data) {
      try {
        // Fetch frame image
        const imageBlob = await fetchFrameImage(apiFrame.imageUrl);

        // Fetch thumbnail if available
        let thumbnailBlob: Blob | undefined;
        if (apiFrame.thumbnailUrl) {
          try {
            thumbnailBlob = await fetchFrameImage(apiFrame.thumbnailUrl);
          } catch (err) {
            console.warn(`Failed to fetch thumbnail for frame ${apiFrame.frameId}, continuing without it`);
          }
        }

        const localFrame: LocalFrame = {
          frameId: apiFrame.frameId,
          name: apiFrame.name,
          categoryId: apiFrame.frameCategoryId,
          imageBlob,
          thumbnailBlob,
          paperType: apiFrame.paperType,
          isActive: apiFrame.isActive,
          syncedAt: Date.now(),
        };

        localFrames.push(localFrame);
      } catch (error) {
        console.error(`Failed to process frame ${apiFrame.frameId}:`, error);
        // Continue with other frames
      }
    }

    // Cache locally only if we have frames (empty is valid when API has no frames for this paper type)
    if (localFrames.length > 0) {
      await addFrames(localFrames);
      console.log(`Cached ${localFrames.length} frames for ${paperType}`);
    } else {
      console.log(`No frames to cache for ${paperType} (API returned none or all failed to process)`);
    }

    return localFrames;
  } catch (error) {
    console.error('Failed to load frames from API:', error);

    // If we have any cached frames (even if not matching paper type), return empty array
    // This allows the UI to show an appropriate error message
    if (error instanceof Error) {
      throw new Error(`Failed to load frames: ${error.message}`);
    }
    throw new Error('Failed to load frames from API');
  }
}
